/**
 * Catalog-aware migration tools. Two handlers, both with `dry_run: true` default.
 *   - `migrate_type`: bulk-retype nodes, then re-infer affected edge types.
 *   - `migrate_cross_edges`: move cross-product edges from `upg.edges` to
 *     `upg.cross_product_edges`.
 */

import {
  UPG_TYPES_SET,
  UPG_CROSS_EDGE_TYPES,
  resolveContainmentEdge,
} from '@unified-product-graph/core'
import { nanoid } from 'nanoid'
import { type ToolHandler, text, textError } from '../lib/server-context.js'

// ─── Pool accessor (mirrors validation.ts pattern) ────────────────────────────

interface PoolLike {
  query<T = unknown>(sql: string, values?: unknown[]): Promise<{ rows: T[] }>
  connect(): Promise<ClientLike>
}

interface ClientLike {
  query<T = unknown>(sql: string, values?: unknown[]): Promise<{ rows: T[]; rowCount?: number | null }>
  release(): void
}

function getPool(store: object): PoolLike {
  return (store as unknown as { pool: PoolLike }).pool
}

// ─── Internal row types ───────────────────────────────────────────────────────

interface AffectedEdgeRow {
  id: string
  source: string
  target: string
  type: string
  source_type: string
  target_type: string
}

interface CrossEdgeRow {
  id: string
  source: string
  target: string
  type: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Prefix for cross-product edge IDs generated by this tool. */
function crossEdgeId(): string {
  return `ce_${nanoid(16)}`
}

/**
 * Migrate all nodes of one entity type to another within a product.
 * After renaming nodes, re-infers edge types for all edges connected to the
 * migrated nodes (because the correct edge type depends on the
 * source/target entity type pair).
 *
 * - `product_id`: Product to scope the migration to.
 * - `from_type`: Current entity type to migrate away from.
 * - `to_type`: Target entity type. Must be a valid UPG entity type.
 * - `dry_run` (default `true`): When true, counts affected rows without mutating.
 *
 * @returns JSON: `{ from_type, to_type, affected_nodes, retyped_edges, dry_run }`.
 * @throws textError when `product_id`, `from_type`, or `to_type`
 *   is missing, or when `to_type` is not a known UPG entity type.
 * @atomicity atomic-with-rollback (false only)
 * @warning Default is `dry_run: true`; pass `dry_run: false` to commit.
 *   Idempotent on retry: a second `dry_run: false` finds zero `from_type`
 *   nodes and reports `affected_nodes: 0`. Edge re-inference uses
 *   `resolveContainmentEdge`, so already-canonical edges may change type
 *   when the new pair has a different canonical edge.
 * @see validate_graph
 * @see migrate_cross_edges
 * @see rename_edge_type
 * @see list_type_migrations
 * @see list_entity_types
 */
export const migrateType: ToolHandler = async (args, { store }) => {
  if (!args.product_id) return textError('Missing required parameter: product_id')
  if (!args.from_type) return textError('Missing required parameter: from_type')
  if (!args.to_type) return textError('Missing required parameter: to_type')

  const productId = args.product_id as string
  const fromType = args.from_type as string
  const toType = args.to_type as string
  const dryRun = (args.dry_run as boolean | undefined) ?? true

  // Validate to_type is a known UPG entity type.
  if (!UPG_TYPES_SET.has(toType)) {
    return textError(
      `Unknown entity type: "${toType}". ` +
      `Run get_entity_schema or validate_graph to inspect valid types.`,
    )
  }

  const pool = getPool(store)

  if (dryRun) {
    const { rows } = await pool.query<{ count: string }>(
      `SELECT COUNT(*)::text AS count FROM upg.nodes WHERE product_id = $1 AND type = $2`,
      [productId, fromType],
    )
    const affected = parseInt(rows[0]?.count ?? '0', 10)
    return text(JSON.stringify({
      from_type: fromType,
      to_type: toType,
      affected_nodes: affected,
      dry_run: true,
    }, null, 2))
  }

  // Apply mode: run inside a transaction.
  const client = await pool.connect()
  try {
    await client.query('BEGIN')

    // 1. Retype nodes.
    const { rowCount: updatedNodes } = await client.query(
      `UPDATE upg.nodes SET type = $1 WHERE product_id = $2 AND type = $3`,
      [toType, productId, fromType],
    )
    const affectedNodes = updatedNodes ?? 0

    // 2. Re-infer edge types for all edges connected to migrated nodes.
    //    Run AFTER the node UPDATE so source_type/target_type reflect the new type.
    const { rows: affectedEdges } = await client.query<AffectedEdgeRow>(
      `SELECT e.id, e.source, e.target, e.type,
              ns.type AS source_type, nt.type AS target_type
       FROM upg.edges e
       JOIN upg.nodes ns ON ns.id = e.source
       JOIN upg.nodes nt ON nt.id = e.target
       WHERE e.product_id = $1
         AND (ns.id IN (SELECT id FROM upg.nodes WHERE product_id = $1 AND type = $2)
           OR nt.id IN (SELECT id FROM upg.nodes WHERE product_id = $1 AND type = $2))`,
      [productId, toType],
    )

    let retypedEdges = 0
    for (const edge of affectedEdges) {
      const newType = resolveContainmentEdge(edge.source_type, edge.target_type)
      if (newType && newType !== edge.type) {
        await client.query(
          `UPDATE upg.edges SET type = $1 WHERE id = $2`,
          [newType, edge.id],
        )
        retypedEdges++
      }
    }

    await client.query('COMMIT')

    return text(JSON.stringify({
      from_type: fromType,
      to_type: toType,
      affected_nodes: affectedNodes,
      retyped_edges: retypedEdges,
      dry_run: false,
    }, null, 2))
  } catch (err) {
    await client.query('ROLLBACK')
    throw err
  } finally {
    client.release()
  }
}

/**
 * Find edges in `upg.edges` that carry a cross-product edge type and move
 * them to `upg.cross_product_edges`. Cross-product edge types belong in
 * the cross-product table; this tool corrects data that predates the
 * tightening introduced in.
 *
 * - `product_id`: Product to scope the migration to.
 * - `dry_run` (default `true`): When true, reports what would move without moving it.
 *
 * @returns JSON: `{ product_id, migrated, count, dry_run }`.
 * @throws textError when `product_id` is missing.
 * @atomicity atomic-with-rollback (false only)
 * @warning Default is `dry_run: true`; pass `dry_run: false` to commit.
 *   Idempotent on retry: a second `dry_run: false` finds zero matching
 *   intra-product rows and reports `count: 0`. Migrated rows get a fresh
 *   `ce_*` id while the original edge id falls away; the audit log retains
 *   the trail.
 * @see list_cross_edge_types
 * @see list_portfolio_cross_edges
 * @see validate_graph
 * @see migrate_type
 */
export const migrateCrossEdges: ToolHandler = async (args, { store }) => {
  if (!args.product_id) return textError('Missing required parameter: product_id')

  const productId = args.product_id as string
  const dryRun = (args.dry_run as boolean | undefined) ?? true

  const pool = getPool(store)
  const crossEdgeTypes = Array.from(UPG_CROSS_EDGE_TYPES)

  if (dryRun) {
    const { rows } = await pool.query<CrossEdgeRow>(
      `SELECT id, source, target, type
       FROM upg.edges
       WHERE product_id = $1
         AND type = ANY($2::text[])`,
      [productId, crossEdgeTypes],
    )
    return text(JSON.stringify({
      product_id: productId,
      migrated: rows,
      count: rows.length,
      dry_run: true,
    }, null, 2))
  }

  // Apply mode: run inside a transaction.
  const client = await pool.connect()
  try {
    await client.query('BEGIN')

    // 1. Select the edges to migrate.
    const { rows: edgesToMigrate } = await client.query<CrossEdgeRow>(
      `SELECT id, source, target, type
       FROM upg.edges
       WHERE product_id = $1
         AND type = ANY($2::text[])`,
      [productId, crossEdgeTypes],
    )

    // 2. Insert into cross_product_edges.
    for (const edge of edgesToMigrate) {
      await client.query(
        `INSERT INTO upg.cross_product_edges (id, source, target, type, created_by_product_id)
         VALUES ($1, $2, $3, $4, $5)`,
        [crossEdgeId(), edge.source, edge.target, edge.type, productId],
      )
    }

    // 3. Delete from upg.edges.
    if (edgesToMigrate.length > 0) {
      const ids = edgesToMigrate.map((e) => e.id)
      await client.query(
        `DELETE FROM upg.edges WHERE id = ANY($1::text[])`,
        [ids],
      )
    }

    await client.query('COMMIT')

    return text(JSON.stringify({
      product_id: productId,
      migrated: edgesToMigrate,
      count: edgesToMigrate.length,
      dry_run: false,
    }, null, 2))
  } catch (err) {
    await client.query('ROLLBACK')
    throw err
  } finally {
    client.release()
  }
}
